import { css } from "@emotion/react";
import { useTranslations } from "next-intl";

import {
  Colors,
  Link,
  SkeletonBlock,
  SkeletonLoader,
} from "@zapier/design-system";

import { HeldReason } from "@zapier/reporting/src/graphql-types";
import useHeldApp from "./useHeldApp";

type Props = {
  reason: HeldReason;
  taskId: string;
};

const rootStyle = css`
  width: 250px;
  color: ${Colors.GrayWarm2};
`;

export function reasonNeedsHeldApp(reason: HeldReason) {
  return [
    HeldReason.access_blacklisted_app,
    HeldReason.blacklisted_app,
    HeldReason.revoked_auth_access,
    HeldReason.stale_auth,
  ].includes(reason);
}

function HeldStatusTip(props: Props) {
  const t = useTranslations("held-status-tip");
  const needsHeldApp = reasonNeedsHeldApp(props.reason);
  const { app, isLoading } = useHeldApp({
    skip: !needsHeldApp,
    taskId: props.taskId,
  });
  const appName = app?.name || t("default-app-name");

  if (needsHeldApp && isLoading) {
    return (
      <SkeletonLoader>
        <SkeletonBlock height="22px" width="250px" />
      </SkeletonLoader>
    );
  }

  switch (props.reason) {
    case HeldReason.access_blacklisted_app:
      return (
        <div css={rootStyle}>
          {t("access-blacklisted-app", { appName })}{" "}
          <Link href="https://zapier.com/help/account/companies/manage-access-to-apps-in-zapier">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.blacklisted_app:
      return (
        <div css={rootStyle}>
          {t("blacklisted-app", { appName })}{" "}
          <Link href="https://zapier.com/help/account/billing-pricing/understand-premium-apps">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.dedupe_change:
      return <div css={rootStyle}>{t("dedupe-change")}</div>;
    case HeldReason.paymentIssue:
      return (
        <div css={rootStyle}>
          {t("payment-issue")}{" "}
          <Link href="https://zapier.com/app/settings/billing">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.stale_auth:
      return (
        <div css={rootStyle}>
          {t("stale-auth", { appName })}{" "}
          <Link href="https://zapier.com/app/connections">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.revoked_auth_access:
      return (
        <div css={rootStyle}>
          {t("revoked-auth-access", { appName })}{" "}
          <Link href="https://zapier.com/help/manage/app-connections/share-app-connections-with-your-team">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.step_limit:
      return (
        <div css={rootStyle}>
          {t("step-limit")}{" "}
          <Link href="https://zapier.com/help/create/basics/learn-key-concepts-in-zapier#step-14">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.task_limit:
      return (
        <div css={rootStyle}>
          {t("task-limit")}{" "}
          <Link href="https://zapier.com/help/manage/tasks/learn-about-tasks-in-zapier#step-2">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.throttled:
      return (
        <div css={rootStyle}>
          {t("throttled")}{" "}
          <Link href="https://zapier.com/help/troubleshoot/behavior/rate-limits-and-throttling-in-zapier#step-1">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.too_many:
      return (
        <div css={rootStyle}>
          {t("too-many")}{" "}
          <Link href="https://zapier.com/help/troubleshoot/behavior/rate-limits-and-throttling-in-zapier#step-3">
            {t("more-info")}
          </Link>
        </div>
      );
    case HeldReason.unknown:
      return (
        <div css={rootStyle}>
          {t("unknown")}{" "}
          <Link href="https://zapier.com/app/get-help">{t("more-info")}</Link>
        </div>
      );
  }

  return null;
}

export default HeldStatusTip;
